import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useEventSafe } from "./EventContext";
import { useAttendee } from "./AttendeeContext";
import { authenticatedApi } from "../lib/api";

const FavoritesContext = createContext();

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
}

export function FavoritesProvider({ children }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { event } = useEventSafe();
  const { attendee } = useAttendee();

  // Load favorites when event changes, but only if user is authenticated
  useEffect(() => {
    if (event?.slug && attendee) {
      loadFavorites();
    } else {
      setFavorites([]);
    }
  }, [event?.slug, attendee]);

  const loadFavorites = useCallback(async () => {
    if (!event?.slug || !attendee) return;

    try {
      setLoading(true);
      setError(null);
      const favoritesData = await authenticatedApi.getFavorites(event.slug);
      setFavorites(favoritesData);
    } catch (error) {
      setError(error.message || "Failed to load favorites");
    } finally {
      setLoading(false);
    }
  }, [event?.slug, attendee]);

  const addFavorite = useCallback(
    async (favoritableType, favoritableId) => {
      if (!event?.slug || !attendee) return;

      try {
        const newFavorite = await authenticatedApi.createFavorite(
          event.slug,
          favoritableType,
          favoritableId
        );
        setFavorites((prev) => [...prev, newFavorite]);
        return newFavorite;
      } catch (error) {
        setError(error.message || "Failed to add favorite");
        throw error;
      }
    },
    [event?.slug, attendee]
  );

  const removeFavorite = useCallback(
    async (favoriteId) => {
      if (!event?.slug || !attendee) return;

      try {
        await authenticatedApi.deleteFavorite(event.slug, favoriteId);
        setFavorites((prev) => prev.filter((fav) => fav.id !== favoriteId));
      } catch (error) {
        setError(error.message || "Failed to remove favorite");
        throw error;
      }
    },
    [event?.slug, attendee]
  );

  const toggleFavorite = useCallback(
    async (favoritableType, favoritableId) => {
      if (!event?.slug || !attendee) return;

      // Check if already favorited
      const existingFavorite = favorites.find(
        (fav) =>
          fav.favoritable_type === favoritableType &&
          fav.favoritable_id === favoritableId
      );

      if (existingFavorite) {
        await removeFavorite(existingFavorite.id);
        return false; // Removed
      } else {
        await addFavorite(favoritableType, favoritableId);
        return true; // Added
      }
    },
    [event?.slug, attendee, favorites, addFavorite, removeFavorite]
  );

  const isFavorited = useCallback(
    (favoritableType, favoritableId) => {
      return favorites.some(
        (fav) =>
          fav.favoritable_type === favoritableType &&
          fav.favoritable_id === favoritableId
      );
    },
    [favorites]
  );

  const getFavoriteId = useCallback(
    (favoritableType, favoritableId) => {
      const favorite = favorites.find(
        (fav) =>
          fav.favoritable_type === favoritableType &&
          fav.favoritable_id === favoritableId
      );
      return favorite?.id;
    },
    [favorites]
  );

  const value = {
    favorites,
    loading,
    error,
    loadFavorites,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorited,
    getFavoriteId,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}
